import { Auth0AccountRole } from "client/openapi";
import { RecentKind } from "client/openapi/models/RecentKind";
import type { RecentPerson } from "client/openapi/models/RecentPerson";
import { PeopleService } from "client/openapi/services/PeopleService";
import { useContext, useEffect, useState } from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";

/**
 * The people pickers' Recent group: who the signed-in admin dealt with
 * lately, listed while nothing is typed. The server knows the messages,
 * forms and tasks; this browser keeps the profile pages the admin opened.
 */

/** The most people a Recent group lists. */
export const RECENT_SHOWN = 8;

/** The group's heading. */
export const RECENT_LABEL = "Recent";

/** Asked of the server: a few more than shown, for those already chosen. */
const RECENT_FETCHED = 12;

/** The profile pages a browser keeps, per organization and admin. */
const VISITS_KEPT = 50;

const VISIT_KINDS = [RecentKind.STUDENT, RecentKind.PARENT, RecentKind.TUTOR];

type Visit = { kind: RecentKind; id: number; at: string };

/**
 * Requests on their way, by what they ask: pickers shown together (a task's
 * Assign to and About) share one.
 */
const asking = new Map<string, Promise<RecentPerson[]>>();

function askServer(
  query: Parameters<typeof PeopleService.getRecentPeople>[0]
): Promise<RecentPerson[]> {
  const key = JSON.stringify(query);
  let answer = asking.get(key);
  if (!answer) {
    answer = PeopleService.getRecentPeople(query);
    asking.set(key, answer);
    const forget = () => asking.delete(key);
    answer.then(forget, forget);
  }
  return answer;
}

const visitsKey = (orgId: number, accountId: string) =>
  `tadpole.recent-visits.${orgId}.${accountId}`;

/** This browser's profile page visits, newest first. Empty if unreadable. */
function readVisits(orgId: number, accountId: string): Visit[] {
  try {
    const saved = JSON.parse(
      window.localStorage.getItem(visitsKey(orgId, accountId)) ?? "[]"
    );
    return Array.isArray(saved)
      ? saved.filter(
          (visit) =>
            VISIT_KINDS.includes(visit?.kind) &&
            Number.isInteger(visit.id) &&
            typeof visit.at === "string"
        )
      : [];
  } catch {
    return [];
  }
}

/** A person's key in a picker: kind and id (an admin's account id). */
export const recentKey = (kind: string, id: string | number) => `${kind}:${id}`;

const keyOfRecent = (person: RecentPerson) =>
  recentKey(person.kind, person.account_id ?? person.id ?? "");

/** The org and account the Recent group is for: signed-in admins only. */
function useAdmin() {
  const { account, currently_selected_organization, currently_selected_role } =
    useContext(OrgRolesAndAccountContext);
  const isAdmin =
    currently_selected_role === Auth0AccountRole.ORG_ADMIN &&
    !!currently_selected_organization &&
    !!account?.reference_id;
  return isAdmin
    ? {
        orgId: currently_selected_organization as number,
        accountId: account?.reference_id as string,
      }
    : null;
}

/**
 * Remembers that the signed-in admin opened this person's profile page, in
 * this browser (newest first, the oldest dropped). Nothing for others.
 */
export function useRememberVisit(kind: RecentKind, id: number | undefined) {
  const admin = useAdmin();
  const orgId = admin?.orgId;
  const accountId = admin?.accountId;

  useEffect(() => {
    if (!orgId || !accountId || !id || !Number.isInteger(id)) return;
    try {
      const visits = [
        { kind, id, at: new Date().toISOString() },
        ...readVisits(orgId, accountId).filter(
          (visit) => !(visit.kind === kind && visit.id === id)
        ),
      ].slice(0, VISITS_KEPT);
      window.localStorage.setItem(
        visitsKey(orgId, accountId),
        JSON.stringify(visits)
      );
    } catch {
      // Storage off or full: the server's people still list.
    }
  }, [orgId, accountId, kind, id]);
}

/**
 * The signed-in admin's recent people of these kinds, newest first: the
 * server's, and the profile pages opened in this browser, each person once
 * at their newest time. Empty for others, while loading, and on failure.
 */
export function useRecentPeople(kinds: readonly string[]): RecentPerson[] {
  const admin = useAdmin();
  const orgId = admin?.orgId;
  const accountId = admin?.accountId;
  const [people, setPeople] = useState<RecentPerson[]>([]);
  // One request per kinds asked, whatever array they come in.
  const asked = Array.from(new Set(kinds)).sort().join(",");

  useEffect(() => {
    // Nothing from another org, account or kinds while this one is asked.
    setPeople([]);
    const wanted = asked
      .split(",")
      .filter((kind): kind is RecentKind =>
        Object.values(RecentKind).includes(kind as RecentKind)
      );
    if (!orgId || !accountId || wanted.length === 0) return;

    const visits = readVisits(orgId, accountId).filter((visit) =>
      wanted.includes(visit.kind)
    );
    const visited = (kind: RecentKind) =>
      visits.filter((visit) => visit.kind === kind).map((visit) => visit.id);
    const visitedAt = new Map(
      visits.map((visit) => [recentKey(visit.kind, visit.id), visit.at])
    );

    let current = true;
    askServer({
      orgId,
      kinds: wanted,
      limit: RECENT_FETCHED,
      visitedStudents: visited(RecentKind.STUDENT),
      visitedParents: visited(RecentKind.PARENT),
      visitedTutors: visited(RecentKind.TUTOR),
    })
      .then((found) => {
        if (!current) return;
        const time = (at?: string | null) => (at ? Date.parse(at) : 0);
        const merged = found.map((person) => {
          const seen = visitedAt.get(keyOfRecent(person));
          return time(seen) > time(person.at)
            ? { ...person, at: seen }
            : person;
        });
        setPeople(merged.sort((a, b) => time(b.at) - time(a.at)));
      })
      .catch(() => {
        if (current) setPeople([]);
      });
    return () => {
      current = false;
    };
  }, [orgId, accountId, asked]);

  return people;
}

/**
 * The recent people among a picker's options (by `keyOf`, see recentKey),
 * newest first, at most RECENT_SHOWN.
 */
export function recentAmong<T>(
  recent: RecentPerson[],
  options: readonly T[],
  keyOf: (option: T) => string
): T[] {
  const byKey = new Map(options.map((option) => [keyOf(option), option]));
  return recent
    .flatMap((person) => {
      const option = byKey.get(keyOfRecent(person));
      return option ? [option] : [];
    })
    .slice(0, RECENT_SHOWN);
}

/**
 * For a select that has every option it can pick (react-select). Give it
 * `options(all, keyOf)` and `onInputChange`: while nothing is typed, the
 * options are its recent people as one Recent group, or all of them when
 * there are none yet; once something is typed, all of them, for the
 * select's own name search.
 */
export function useRecentOptions(kinds: readonly string[]) {
  const recent = useRecentPeople(kinds);
  const [typed, setTyped] = useState(false);

  return {
    options<T>(all: readonly T[], keyOf: (option: T) => string) {
      const listed = recentAmong(recent, all, keyOf);
      return !typed && listed.length > 0
        ? [{ label: RECENT_LABEL, options: listed }]
        : all;
    },
    onInputChange: (text: string) => setTyped(text.trim() !== ""),
  };
}

/**
 * A recent person as a minimal student or tutor for a picker that searches
 * the server instead of holding everyone: id, organization and name.
 */
export const recentAsPerson = (person: RecentPerson, orgId: number) => ({
  id: person.id as number,
  org_id: orgId,
  first_name: person.first_name ?? "",
  last_name: person.last_name ?? "",
});
